import {forEach, isEqual, transform} from 'lodash';
import moment from 'moment';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {TooltipProps} from 'recharts';

import {BarChart} from 'components';
import {themeConfig} from 'constants/theme';
import {AVAILABILITY_INTERVAL, DAY_INTERVALS_COUNT} from 'constants/working-hours';
import {TRANSLATION_KEYS} from 'locale';
import {WorkingHoursInterval} from 'types';

import {getUsersWorkingHoursBarData} from '../utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.WORKING_HOURS.TIMELINE;
const T_KEYS_TOOLTIP = T_KEYS.BAR_CHART.TOOLTIP;

const COLORS = {
    ASSIGNMENT: themeConfig.colors.lightGray,
    USERS: themeConfig.colors.accentSky
};

const AgentsBarTooltip = (props: TooltipProps) => {
    const {t} = useTranslation();

    if (props?.active) {
        const p = props?.payload[0];
        if (p?.payload) {
            const start = p.payload.start?.format?.('HH:mm');
            const end = p.payload.end?.format?.('HH:mm');
            const matchAssignmentLabel = p.payload.isInAccountWorkingHours ?
                t(T_KEYS_TOOLTIP.MATCH_ASSIGNMENT) : t(T_KEYS_TOOLTIP.NOT_MATCH_ASSIGNMENT);
            return (
                <div className='tw-z-1 tw-bg-light tw-p-4'>
                    <p>{`${t(T_KEYS_TOOLTIP.USERS)}: ${p.value}`}</p>
                    <p>{`${t(T_KEYS_TOOLTIP.INTERVAL_START)}: ${start}. ${t(T_KEYS_TOOLTIP.INTERVAL_END)}: ${end}`}</p>
                    <p>{matchAssignmentLabel}</p>
                </div>
            );
        }
    }

    return null;
};

type Props = {
    usersIntervals: {userName: string, intervals: WorkingHoursInterval[]}[];
    assignmentIntervals: WorkingHoursInterval[];
    timeZone: string;
}

export const BarTimeline: React.FC<Props> = ({usersIntervals, assignmentIntervals, timeZone}) => {
    const {t} = useTranslation();
    const barData = useMemo(() => {
        const mergedUsersIntervals = transform(usersIntervals, (acc, userIntervalWrapper) => {
            acc.push(...userIntervalWrapper.intervals);
        }, []);

        return getUsersWorkingHoursBarData(mergedUsersIntervals, timeZone);
    }, [usersIntervals, timeZone]);

    const isInAccountWorkingHours = useCallback((intervalStart: moment.Moment) => {
        let result = false;

        forEach(assignmentIntervals, (accountInterval) => {
            // if no working hours set for assignment
            if (isEqual(accountInterval.from, accountInterval.to)) {
                result = true;
                return false;
            }

            const accountIntervalStart = moment()
                .startOf('d').set('h', accountInterval.from.hours).set('m', accountInterval.from.minutes - 1);
            const accountIntervalEnd = moment()
                .startOf('d').set('h', accountInterval.to.hours).set('m', accountInterval.to.minutes - 1);

            if (intervalStart.isBetween(accountIntervalStart, accountIntervalEnd)) {
                result = true;
                return false;
            }
        });

        return result;

    }, [assignmentIntervals]);

    const barChartData = useMemo(() => {
        const startDate = moment().startOf('d');
        const data = [];
        for (let i = 0; i < DAY_INTERVALS_COUNT; i++) {
            const key = startDate.format('HH:mm');
            data.push({
                name: key,
                agents: barData?.[key],
                start: startDate.clone(),
                end: startDate.clone().add(AVAILABILITY_INTERVAL, 'm'),
                isInAccountWorkingHours: isInAccountWorkingHours(startDate),
            });
            startDate.add(AVAILABILITY_INTERVAL, 'm');
        }
        return data;
    }, [barData, isInAccountWorkingHours]);

    const agentsBar = useMemo(() => ({
        dataKey: 'agents',
        fill: COLORS.USERS,
        fillOpacity: 0.8,
        strokeWidth: 0,
        background: (item) => {
            if (item.isInAccountWorkingHours) {
                return (
                    <g key={item.x}>
                        <rect
                            fill={COLORS.ASSIGNMENT}
                            fillOpacity={0.6}
                            x={item.x}
                            y={item.y}
                            width={item.width}
                            height={item.height}
                        />
                    </g>

                );
            }
        }
    }), []);

    return (
        <Fragment>
            <div className='tw-flex tw-justify-center tw-mb-4'>
                <div className='tw-flex tw-items-center tw-mx-8'>
                    <div className='tw-w-6 tw-h-6 tw-mr-2' style={{backgroundColor: COLORS.ASSIGNMENT}} />
                    {t(T_KEYS.BAR_CHART.LEGEND.ASSIGNMENT)}
                </div>
                <div className='tw-flex tw-items-center tw-mx-8'>
                    <div className='tw-w-6 tw-h-6 tw-mr-2' style={{backgroundColor: COLORS.USERS}} />
                    {t(T_KEYS.BAR_CHART.LEGEND.USERS)}
                </div>
            </div>
            <BarChart
                data={barChartData}
                barCategoryGap={0}
                margin={{top: 5, right: 0, bottom: 5, left: -30}}
                xAxis={{
                    dataKey: 'name'
                }}
                yAxis={{
                    allowDecimals: false
                }}
                bars={[agentsBar]}
                tooltip={<AgentsBarTooltip />}
            />
        </Fragment>
    );
};